import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { Audit } from '@app/modules/model/audit';

import { toastService } from '@app/modules/common/toast';
import { languageService, languageSupport } from '@app/modules/common/language';
import { syncService } from '@app/modules/sync';
import { viewService } from '@app/modules/view';

import { UiAuditModal } from '@app/components/login/modal/audit/ui-audit-modal';

/************************************/
/* CASH COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-audit-thumbnail',
    templateUrl: 'ui-thumb.html',
    styleUrls: [
      'ui-thumb.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAuditThumbnail extends languageSupport implements OnInit, OnChanges, OnDestroy {    
    @Input('audit') _audit: Audit = null; 

    get MultiplePOS(){
        return this._audit.place.MultiplePOS;
    }

    get CanCard(){
        return this._audit.place.AcceptCard;
    }

    get CanOnline(){
        return this._audit.place.AcceptOnline;        
    }

    /* SII sent status */

    get ShowSII(){
        if (this._audit.place.SendSIIEnabled){
            return (this.Report.sii.ok > 0) || (this.Report.sii.ko > 0);
        }
        return false;   // not enabled
    }

    get SIISending(){
        if (this._audit.auditinfo){
            return this._audit.auditinfo.SIISending;
        }
        return false;
    }

    get SIISuccess(){
        return (this.Report.sii.ko == 0);
    }

    /* BAI sent status */

    get ShowBAI(){
        if (this._audit.place.SendBAIEnabled){
            return (this.Report.bai.ok > 0) || (this.Report.bai.ko > 0);
        }
        return false;   // not enabled
    }

    get BAISending(){
        if (this._audit.auditinfo){
            return this._audit.auditinfo.BAISending;
        }
        return false;
    }

    get BAISuccess(){
        return (this.Report.bai.ko == 0);
    }

    /* VERIFACTU sent status */

    get ShowVFT(){
        if (this._audit.place.SendVFTEnabled){
            return (this.Report.vft.ok > 0) || (this.Report.vft.ko > 0);
        }
        return false;   // not enabled
    }

    get VFTSending(){
        if (this._audit.auditinfo){
            return this._audit.auditinfo.VFTSending;
        }
        return false;
    }

    get VFTSuccess(){
        return (this.Report.vft.ko == 0);
    }

    /* report data */

    Price(value: number){
        let _parts = (Math.abs(value)).toString().split('.');
        let _int = _parts[0];
        let _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : "00";
        let _str = _int + ',' + _dec;
        let _neg = (value < 0); 

        return {
            Int: _int,
            Dec: _dec,
            Str: _str,
            Neg: _neg
        }    
    }

    private _report = null;
    get Report() {
        if (this._report == null){
            let _report = {
                date: null,
                user: null,
                cash: 0,
                card: 0,
                webpay: 0,
                inout: 0,
                sales: 0,
                total: 0,
                sii: {
                    ok: 0,
                    ko: 0 
                },
                bai: {
                    ok: 0,
                    ko: 0
                },
                vft: {
                    ok: 0,
                    ko: 0
                }
            };
    
            // audit date
            _report.date = this._audit.created;
    
            // audit user
            if (this._audit.session?.user){
                _report.user = this._audit.session.user.fullname;
            }
    
            // audit cash in till
            _report.inout = this._audit.cash;
    
            // audit payments
            let _auditinfo = this._audit.auditinfo;
            if (_auditinfo){
                _report.cash = _auditinfo.cash;
                _report.card = _auditinfo.card;
                _report.webpay = _auditinfo.webpay;
            }
    
            _report.sales = _report.cash + _report.card + _report.webpay;
            _report.sales = Math.round(_report.sales * 100) / 100;
            _report.total = _report.inout + _report.cash;
            _report.total = Math.round(_report.total * 100) / 100;
    
            // ticketsii counters
            if (_auditinfo){
                _report.sii = {
                    ok: _auditinfo.ticketsii.ok,
                    ko: _auditinfo.ticketsii.ko
                };
            }

            // ticketbai counters
            if (_auditinfo){
                _report.bai = {
                    ok: _auditinfo.ticketbai.ok,
                    ko: _auditinfo.ticketbai.ko
                };
            }

            // verifactu counters
            if (_auditinfo){
                _report.vft = {
                    ok: _auditinfo.verifactu.ok,
                    ko: _auditinfo.verifactu.ko
                };
            }

            this._report = _report;
        }

        return this._report;
    }

    /*****************************/
    /* STARTS HERE               */
    /*****************************/

    private OnRefreshReport(){
        this._report = null;
        this.change.markForCheck();
    }

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private toast: toastService, private sync: syncService, public view: viewService){
        super(lang, change)
    }

    ngOnInit() {
        // nothing to do    
    }

    private _audit_subscription = null;

    ngOnChanges() {
        if (this._audit_subscription){
            this._audit_subscription.unsubscribe();
            this._audit_subscription = null;
        }

        this._audit_subscription = this._audit.OnRefresh.subscribe(
        data => {
            this.OnRefreshReport();
        });

        // Importes/contadores se muestran de la foto persistida del
        // AUDITINFO (calculada por el till del cierre al cerrar). NO se
        // recomputa en local: ReloadInfo usaba la memoria + el till del
        // dispositivo que MIRA, pintando el cierre de otro TPV con las
        // cifras propias (bug MultiplePOS). El desglose de IVA y la
        // numeracion por serie los recalcula el modal en el servidor.
    }

    ngOnDestroy() {
        super.OnDestroy();

        if (this._audit_subscription){
            this._audit_subscription.unsubscribe();
            this._audit_subscription = null;
        }
    }

    /************************/
    /* EVENTS HANDLER       */
    /************************/

    private _waitmodal = false;
    async PrintReceipt(){
        if (!this._waitmodal){
            this._waitmodal = true;
            const modal = await this.modalCtrl.create({
                component: UiAuditModal,
                cssClass: 'audit-modal',
                componentProps: { 
                    audit: this._audit
                }                                    
            });

            await modal.present();
            
            setTimeout(() => {
                this._waitmodal = false;
            }, 100);
        }
    }

    private _AuditToSII(_audit){
        let _doAuditUrl = 'taxes/audit-sii.php';

        _audit.auditinfo.SIISending = true;
        return new Promise<any>((resolve) => {
            this.sync.DoRequest(_doAuditUrl, { audit: _audit.objid })
            .then((data) => {
                _audit.auditinfo.SIISending = false;

                let _ok = data['ticketsok'].length;
                let _ko = data['ticketsko'].length;

                if ((data['errorcode'] == 0) || (_ok > 0) || (_ko > 0)) {    
                    for(let _ticket of data['ticketsok']){
                        console.info("SII (AEAT) OK: " + _ticket[0])
                    }
                    
                    for(let _ticket of data['ticketsko']){
                        console.warn("SII (AEAT) KO: " + _ticket[0] + " - " + _ticket[1])
                    }

                    if (_audit.auditinfo){
                        _audit.auditinfo.OnSIIResponse(_ok, _ko);
                    }

                    resolve(!_ko);
                    if ((_ok > 0) || (_ko > 0)){
                        this.toast.ShowAlert((!_ko) ? 'success' : 'danger', this.lang.tr('@sii_aeat_request_sent', [ _ok, _ko ]));
                    }
                }
                else {
                    resolve(false);

                    this.toast.ShowAlert('danger', this.lang.tr('@sii_aeat_request_error') + ": " + this.lang.tr('@service_request_error_' + data['errorcode']));
                    if (data['errormesg']){
                        console.error("[SII (AEAT)] " + data['errormesg']);
                    }
                }
            }, error => {
                _audit.auditinfo.SIISending = false;

                console.error("[REQUEST] Error in http request '" + _doAuditUrl + "'");
                resolve(false);
            });             
        });
    }

    private _AuditToBAI(_audit){
        let _doAuditUrl = 'taxes/audit-bai.php';

        _audit.auditinfo.BAISending = true;
        return new Promise<any>((resolve) => {
            this.sync.DoRequest(_doAuditUrl, { audit: _audit.objid })
            .then((data) => {
                _audit.auditinfo.BAISending = false;

                let _ok = data['ticketsok'].length;
                let _ko = data['ticketsko'].length;

                if ((data['errorcode'] == 0) || (_ok > 0) || (_ko > 0)) {    
                    for(let _ticket of data['ticketsok']){
                        console.info("TICKETBAI OK: " + _ticket[0])
                    }
                    
                    for(let _ticket of data['ticketsko']){
                        console.warn("TICKETBAI KO: " + _ticket[0] + " - " + _ticket[1])
                    }

                    if (_audit.auditinfo){
                        _audit.auditinfo.OnBAIResponse(_ok, _ko);
                    }

                    resolve(!_ko);
                    if ((_ok > 0) || (_ko > 0)){
                        this.toast.ShowAlert((!_ko) ? 'success' : 'danger', this.lang.tr('@bai_aeat_request_sent', [ _ok, _ko ]));
                    }                    
                }
                else {
                    resolve(false);

                    this.toast.ShowAlert('danger', this.lang.tr('@bai_aeat_request_error') + ": " + this.lang.tr('@service_request_error_' + data['errorcode']));
                    if (data['errormesg']){
                        console.error("[SII (AEAT)] " + data['errormesg']);
                    }
                }
            }, error => {
                _audit.auditinfo.BAISending = false;

                console.error("[REQUEST] Error in http request '" + _doAuditUrl + "'");
                resolve(false);
            });             
        });
    }

    private _AuditToVFT(_audit){
        let _doAuditUrl = 'taxes/audit-vft.php';

        _audit.auditinfo.BAISending = true;
        return new Promise<any>((resolve) => {
            this.sync.DoRequest(_doAuditUrl, { audit: _audit.objid })
            .then((data) => {
                _audit.auditinfo.BAISending = false;

                let _ok = data['ticketsok'].length;
                let _ko = data['ticketsko'].length;

                if ((data['errorcode'] == 0) || (_ok > 0) || (_ko > 0)) {    
                    for(let _ticket of data['ticketsok']){
                        console.info("VERIFACTU OK: " + _ticket[0])
                    }
                    
                    for(let _ticket of data['ticketsko']){
                        console.warn("VERIFACTU KO: " + _ticket[0] + " - " + _ticket[1])
                    }

                    if (_audit.auditinfo){
                        _audit.auditinfo.OnBAIResponse(_ok, _ko);
                    }

                    resolve(!_ko);
                    if ((_ok > 0) || (_ko > 0)){
                        this.toast.ShowAlert((!_ko) ? 'success' : 'danger', this.lang.tr('@vft_aeat_request_sent', [ _ok, _ko ]));
                    }                    
                }
                else {
                    resolve(false);

                    this.toast.ShowAlert('danger', this.lang.tr('@vft_aeat_request_error') + ": " + this.lang.tr('@service_request_error_' + data['errorcode']));
                    if (data['errormesg']){
                        console.error("[SII (AEAT)] " + data['errormesg']);
                    }
                }
            }, error => {
                _audit.auditinfo.BAISending = false;

                console.error("[REQUEST] Error in http request '" + _doAuditUrl + "'");
                resolve(false);
            });             
        });
    }

    OnSIISend(event){
        event.stopPropagation();
        
        this._AuditToSII(this._audit).then(
        data => {
            this.change.markForCheck();
        });
    }

    OnBAISend(event){
        event.stopPropagation();
        
        this._AuditToBAI(this._audit).then(
        data => {
            this.change.markForCheck();
        });
    }

    OnVFTSend(event){
        event.stopPropagation();
        
        this._AuditToVFT(this._audit).then(
        data => {
            this.change.markForCheck();
        });
    }

}
